// Calls to /api/marketplace, in one place so every surface — the marketplace
// page, a listing, the homepage strip, the dashboard and the sell form — reads
// the same responses the same way.

import { supabase } from '@/lib/supabase';
import { authFetch } from '@/lib/authFetch';

const API_URL = import.meta.env.VITE_API_URL ?? '';

/** A public GET that still sends the token when there is one (owners see more). */
async function publicGet(path) {
  /** @type {Record<string, string>} */
  const headers = {};
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.access_token) headers.Authorization = `Bearer ${session.access_token}`;
  } catch { /* anonymous */ }
  const res = await fetch(`${API_URL}${path}`, { headers });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'Request failed');
  return data;
}

/**
 * @param {{q?: string, category?: string, sort?: string, page?: number, limit?: number, exclude?: string}} [query]
 * @returns {Promise<{listings: object[], total: number, currency: string, symbol: string, locale: string}>}
 */
export function browseListings(query = {}) {
  const params = new URLSearchParams();
  Object.entries(query).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') params.set(k, String(v)); });
  return publicGet(`/api/marketplace/listings?${params}`);
}

export function getListing(id) {
  return publicGet(`/api/marketplace/listings/${encodeURIComponent(id)}`);
}

// Previews are fetched by every card on a page and again by the listing page,
// so each is kept for the life of the tab.
const previews = new Map();

/** The site a listing sells: { title, pages, html, css, js, icon }. */
export function getListingPreview(id) {
  if (!previews.has(id)) {
    const p = publicGet(`/api/marketplace/listings/${encodeURIComponent(id)}/preview`)
      .catch((err) => { previews.delete(id); throw err; });
    previews.set(id, p);
  }
  return previews.get(id);
}

export function getMarketplaceConfig() {
  return publicGet('/api/marketplace/config');
}

/**
 * A description for the sell form, written from the site. Free to the seller.
 * @param {string} websiteId
 * @returns {Promise<{description: string}>}
 */
export function draftListingDescription(websiteId) {
  return authFetch('/api/marketplace/describe', { method: 'POST', body: JSON.stringify({ websiteId }) });
}

export function createListing(body) {
  return authFetch('/api/marketplace/listings', { method: 'POST', body: JSON.stringify(body) });
}

export function updateListing(id, body) {
  previews.delete(id);
  return authFetch(`/api/marketplace/listings/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(body) });
}

export function removeListing(id) {
  previews.delete(id);
  return authFetch(`/api/marketplace/listings/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

export function myListings() {
  return authFetch('/api/marketplace/mine');
}

export function myPurchases() {
  return authFetch('/api/marketplace/purchases');
}

export function canBuyListing(id) {
  return authFetch(`/api/marketplace/listings/${encodeURIComponent(id)}/can-buy`);
}

/** Format a listing price the way the API quoted it. */
export function formatListingPrice(amount, { symbol = '$', locale = 'en-US' } = {}) {
  const n = Number(amount) || 0;
  const decimals = Number.isInteger(n) ? 0 : 2;
  try {
    return symbol + n.toLocaleString(locale, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
  } catch {
    return `${symbol}${n}`;
  }
}

/** What a listing's status means to its seller. */
export const LISTING_STATUS = {
  pending:  { label: 'In review', tone: 'text-amber-300 bg-amber-500/10 border-amber-500/20' },
  active:   { label: 'On sale',   tone: 'text-emerald-300 bg-emerald-500/10 border-emerald-500/20' },
  rejected: { label: 'Not approved', tone: 'text-red-300 bg-red-500/10 border-red-500/20' },
  sold:     { label: 'Sold',      tone: 'text-sky-300 bg-sky-500/10 border-sky-500/20' },
  removed:  { label: 'Removed',   tone: 'text-zinc-400 bg-zinc-800 border-zinc-700' },
};
